import classNames from "classnames";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { scrollTo } from "seamless-scroll-polyfill";

import styles from "./AscTrueOrFalseActivity.module.scss";
import { ClassicChevronLeftIcon, ClassicChevronRightIcon } from "../AscIcons";
import AscButton from "../AscButton/AscButton";
import AscProgressBar from "../AscProgressBar/AscProgressBar";
import AscTrueOrFalseQuestionItem from "./AscTrueOrFalseQuestionItem/AscTrueOrFalseQuestionItem";
import AscCorrectAnswersCard from "../AscCards/AscCorrectAnswersCard/AscCorrectAnswersCard";
import { RequiredExceptKeys } from "../../lib/models";
import {
  AscTrueOrFalseAnswer,
  AscTrueOrFalseQuestion,
} from "@ascendnow/shared";

export interface AscTrueOrFalseActivityProps {
  className?: string;
  questions: AscTrueOrFalseQuestion[];
  onAttempt?: (
    answers: {
      id: string;
      answer: boolean;
    }[]
  ) => Promise<
    RequiredExceptKeys<AscTrueOrFalseAnswer, "description">[] | undefined
  >;
  disabled?: boolean;
  completed?: boolean;
  loading?: boolean;
  response?: AscTrueOrFalseAnswer[];
  onTouched?: () => void;
}

export default function AscTrueOrFalseActivity({
  className,
  questions,
  disabled = false,
  completed = false,
  loading = false,
  onAttempt,
  response,
  onTouched,
}: AscTrueOrFalseActivityProps) {
  //refs
  const inputRef = useRef<HTMLInputElement>(null);
  const questionItemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const questionsListContainerRef = useRef<HTMLDivElement>(null);
  //state
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<AscTrueOrFalseAnswer[]>([]);
  const [showAnswersLoading, setShowAnswersLoading] = useState(false);
  const [showCorrectAnswers, setShowCorrectAnswers] = useState(false);
  const answeredQuestions = useMemo(
    () =>
      completed
        ? questions?.length
        : answers?.filter((answer) => answer?.answer !== undefined).length,
    [questions, answers, completed]
  );
  const isOnLastQuestion = activeQuestionIndex === questions?.length - 1;

  function handleAnswerChange(questionId: string, answer: boolean | undefined) {
    const newAnswers = [...answers];
    const questionIndex = questions.findIndex(
      (question) => question?.id === questionId
    );

    if (questionIndex === -1) {
      return;
    }

    if (newAnswers[questionIndex]) {
      newAnswers[questionIndex].answer = answer;
    } else {
      newAnswers[questionIndex] = {
        answer,
        questionId,
      };
    }
    setAnswers(newAnswers);
    onTouched?.();
  }

  async function handleAttempt(
    answers: Required<Pick<AscTrueOrFalseAnswer, "questionId" | "answer">>[]
  ) {
    setShowAnswersLoading(true);
    const _answers = await onAttempt?.(
      answers?.map((a) => ({
        id: a?.questionId,
        answer: a?.answer,
      }))
    );

    if (_answers) {
      setAnswers(_answers);
    }
    setShowAnswersLoading(false);
  }

  async function handleShowCorrectAnswers() {
    /**
     * We are sure that answers has a type of Required<Pick<AscTrueOrFalseAnswer, "questionId" | "answer">>[]
     * Because the handleShowCorrectAnswers handles the click event of the Show answers button which is
     * disabled if the above is not satisfied, hence we are using as.
     */
    await handleAttempt(
      answers as Required<Pick<AscTrueOrFalseAnswer, "questionId" | "answer">>[]
    );
    setShowCorrectAnswers(true);
  }
  function handleCloseCorrectAnswers() {
    setShowCorrectAnswers(false);
    setAnswers([]);
    setActiveQuestionIndex(0);
  }

  function onPrevious() {
    if (activeQuestionIndex === 0) {
      return;
    }
    setActiveQuestionIndex(activeQuestionIndex - 1);
  }

  function onNext() {
    if (activeQuestionIndex >= questions?.length - 1) {
      return;
    }
    setActiveQuestionIndex(activeQuestionIndex + 1);
  }

  function scrollToElement(container: HTMLElement, smoothing = true): void {
    const containerWidth = container.clientWidth;
    const position = (containerWidth + 32) * activeQuestionIndex;

    scrollTo(container, {
      left: position,
      behavior: smoothing ? "smooth" : undefined,
    });
  }

  function onNavigate(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === "ArrowLeft") {
      onPrevious();
    }
    if (event.key === "ArrowRight") {
      onNext();
    }
  }

  function resizeQuestionItems() {
    questionItemRefs?.current?.forEach((item) => {
      if (!item) {
        return;
      }
      item.style.width = `${
        questionsListContainerRef?.current?.clientWidth || 0
      }px`;
    });
    setTimeout(() => {
      if (!questionsListContainerRef?.current) {
        return;
      }
      scrollToElement(questionsListContainerRef?.current);
    }, 1000);
  }

  useEffect(() => {
    if (!questionsListContainerRef?.current) {
      return;
    }
    scrollToElement(questionsListContainerRef?.current);
    inputRef?.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQuestionIndex]);

  useEffect(() => {
    // if the question is not answered and checked, we don't want to change the active question
    if (answers?.[activeQuestionIndex]?.correctAnswer === undefined) {
      return;
    }
    // find the next unanswered question
    const nextQuestionIndex = questions.findIndex(
      (answer, index) =>
        index > activeQuestionIndex && answers?.[index] === undefined
    );

    if (nextQuestionIndex !== -1) {
      setTimeout(() => {
        setActiveQuestionIndex(nextQuestionIndex);
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers]);

  useEffect(() => {
    if (loading) {
      setActiveQuestionIndex(0);
    }
  }, [loading]);

  useEffect(() => {
    resizeQuestionItems();
    window?.addEventListener("resize", resizeQuestionItems);

    return () => {
      window.removeEventListener("resize", resizeQuestionItems);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionItemRefs]);

  useEffect(() => {
    if (!response) {
      return;
    }
    setAnswers(response);
  }, [response]);

  return (
    <div
      data-testid="asc-true-or-false-activity"
      className={classNames(styles.ground, className)}
      onClick={() => {
        inputRef?.current?.focus();
      }}
    >
      <div
        className={classNames(styles.questionsContainer)}
        onClick={() => {
          inputRef?.current?.focus();
        }}
      >
        <div
          className={styles.questionsList}
          ref={questionsListContainerRef}
          onKeyDown={onNavigate}
        >
          <div className={styles.innerList}>
            {questions?.map((question, index) => (
              <AscTrueOrFalseQuestionItem
                loading={loading}
                className={classNames(styles.questionItem, {
                  [styles.activeQuestion]:
                    question?.id === questions?.[activeQuestionIndex]?.id,
                })}
                ref={(ref) => {
                  if (questionItemRefs.current) {
                    questionItemRefs.current[index] = ref;
                  }
                }}
                onAnswerChange={handleAnswerChange}
                id={question?.id}
                key={question?.id}
                answerLoading={showAnswersLoading || loading}
                disabled={
                  disabled ||
                  completed ||
                  answers?.[index]?.correctAnswer !== undefined
                }
                question={question?.question}
                coverImageUrl={question?.coverImageUrl}
                answer={answers[index]?.answer}
              />
            ))}
          </div>
        </div>
        <div className={styles.navigation}>
          <input
            className={styles.hiddenNavigationInput}
            ref={inputRef}
            onKeyDown={onNavigate}
            inputMode="none"
          />
          <AscButton
            variant="lightGray"
            className={classNames(styles.previousButton, {
              [styles.hidden]: activeQuestionIndex === 0,
            })}
            icon={<ClassicChevronLeftIcon />}
            disabled={activeQuestionIndex === 0}
            onClick={onPrevious}
            squarishIcon
          />

          <AscProgressBar
            value={answeredQuestions}
            max={questions?.length}
            fullWidth
          />

          <AscButton
            variant="lightGray"
            className={classNames(styles.nextButton, {
              [styles.hidden]: isOnLastQuestion,
            })}
            icon={<ClassicChevronRightIcon />}
            loading={showAnswersLoading}
            disabled={isOnLastQuestion}
            squarishIcon
            onClick={onNext}
          />
        </div>
      </div>

      <AscCorrectAnswersCard
        loading={showAnswersLoading || loading}
        attemptsCompleted={completed}
        shouldGoNext={!isOnLastQuestion}
        onNext={onNext}
        showCorrectAnswers={showCorrectAnswers}
        onAttempt={handleShowCorrectAnswers}
        onTryAgain={handleCloseCorrectAnswers}
        checkAnswerText={isOnLastQuestion ? "Submit" : "Next"}
        tryAgainText={"Try again"}
        disabled={
          showAnswersLoading ||
          loading ||
          ((answers?.length !== questions?.length ||
            answers?.some((answer) => answer?.answer === undefined)) &&
            isOnLastQuestion)
        }
        className={classNames(styles.correctAnswersCard)}
        correctAnswerContainerClassName={styles.correctAnswerContainer}
        answers={answers?.map((correctAnswer, index) => (
          <span
            key={index}
            className={classNames(styles.answerItem, {
              [styles.correct]: correctAnswer?.correct,
            })}
          >
            {`Question ${index + 1}: ${
              correctAnswer?.description || correctAnswer?.correctAnswer
            }`}
          </span>
        ))}
      />
    </div>
  );
}
